// Print-label helpers: Code 39 barcode rendering + label layout/printing.
//
// Ported from the standalone "Pencil Barcode Label Maker" tool
// (see /printerLable in this workspace) — same Code 39 bar patterns and the
// same "draw a high-DPI canvas, then size it in real mm at print time" trick
// so labels come out crisp on Zebra-class desktop label printers.

export const EXPORT_DPI = 300;

export const LABEL_PRESETS = [
  { id: '38x55',   label: '38 × 55 mm — compact product', widthMm: 38,  heightMm: 55  },
  { id: '50x70',   label: '50 × 70 mm — product tag',      widthMm: 50,  heightMm: 70  },
  { id: '51x76',   label: '51 × 76 mm / 2 × 3 in',          widthMm: 51,  heightMm: 76  },
  { id: '76x102',  label: '76 × 102 mm / 3 × 4 in',         widthMm: 76,  heightMm: 102 },
  { id: '102x152', label: '102 × 152 mm / 4 × 6 in',        widthMm: 102, heightMm: 152 },
];

export const DEFAULT_LABEL_PRESET_ID = LABEL_PRESETS[0].id;

export function getLabelPreset(id) {
  return LABEL_PRESETS.find(p => p.id === id) || LABEL_PRESETS[0];
}

// Pencil-sketch photo style — ported from the reference "Pencil Barcode
// Label Maker" tool so the same controls (contrast / shadow softness /
// background removal) are available here. Thermal label printers are
// monochrome, so a hand-drawn-looking sketch reproduces far better than a
// plain grayscale photo.
export const DEFAULT_SKETCH_ENABLED = true;
export const DEFAULT_SKETCH_CONTRAST = 72;
export const DEFAULT_SKETCH_SHADE = 44;
export const DEFAULT_REMOVE_BACKGROUND = true;
const SKETCH_WORK_SIZE = 720;

// Code 39 bar patterns — "n" = narrow, "w" = wide, alternating bar/space
// (5 bars + 4 spaces per character). Digits + "*" (start/stop) are all we
// need since order numbers are numeric.
const CODE39_PATTERNS = {
  '0': 'nnnwwnwnw', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', '*': 'nwnnwnwnn',
};

export function sanitizeBarcodeDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

// Extracts a printable order code (digits only, for the barcode) and a
// human-readable label (the raw order number, for the text under the bars).
export function getOrderBarcodeValue(orderNumber) {
  const digits = sanitizeBarcodeDigits(orderNumber);
  return digits || '0';
}

export function drawCode39Barcode(ctx, value, x, y, width, height, { showText = true } = {}) {
  const code = sanitizeBarcodeDigits(value) || '0';
  const fullCode = `*${code}*`;
  const quietUnits = 10;
  const totalUnits = fullCode.length * 12 + (fullCode.length - 1) + quietUnits * 2;
  const narrow = Math.max(2, Math.floor(width / totalUnits));
  const wide = narrow * 2;
  const actualWidth = fullCode.length * 12 * narrow + (fullCode.length - 1) * narrow + quietUnits * 2 * narrow;
  let cursor = Math.round(x + (width - actualWidth) / 2 + quietUnits * narrow);
  const barHeight = Math.round(height * (showText ? 0.72 : 0.92));

  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, width, height);
  ctx.fillStyle = '#000000';

  for (const char of fullCode) {
    const pattern = CODE39_PATTERNS[char] || CODE39_PATTERNS['0'];

    for (let i = 0; i < pattern.length; i += 1) {
      const lineWidth = pattern[i] === 'w' ? wide : narrow;

      if (i % 2 === 0) {
        ctx.fillRect(cursor, y, lineWidth, barHeight);
      }

      cursor += lineWidth;
    }

    cursor += narrow;
  }

  if (showText) {
    ctx.font = `700 ${Math.max(18, Math.round(height * 0.26))}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(code, x + width / 2, y + height - Math.max(4, Math.round(height * 0.04)));
  }

  ctx.restore();
}

export function mmToPx(mm, dpi = EXPORT_DPI) {
  return Math.round((mm / 25.4) * dpi);
}

function getLabelLayout(widthPx, heightPx) {
  const margin = Math.round(Math.min(widthPx, heightPx) * 0.06);
  const gap = Math.round(heightPx * 0.03);
  const headerHeight = Math.round(heightPx * 0.09);
  const barcodeHeight = clamp(Math.round(heightPx * 0.16), mmToPx(9), mmToPx(20));
  const barcodeWidth = widthPx - margin * 2;
  const photoLimitHeight = heightPx - margin * 2 - gap * 2 - headerHeight - barcodeHeight;
  const photoSize = Math.max(60, Math.min(widthPx - margin * 2, photoLimitHeight));
  const headerY = margin;
  const photoX = Math.round((widthPx - photoSize) / 2);
  const photoY = Math.round(headerY + headerHeight + gap);
  const barcodeX = margin;
  const barcodeY = Math.round(photoY + photoSize + gap);

  return {
    width: widthPx, height: heightPx, margin,
    headerY, headerHeight,
    photoX, photoY, photoSize,
    barcodeX, barcodeY, barcodeWidth, barcodeHeight,
  };
}

function luminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function colorDistance(data, index, color) {
  const dr = data[index] - color.r;
  const dg = data[index + 1] - color.g;
  const db = data[index + 2] - color.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function isForeground(distance, gray, background, threshold) {
  if (background.isBright) return distance > threshold && gray < 248;
  return distance > threshold && gray > 10;
}

function sampleBackground(data, width, height) {
  const samples = [];
  const pad = Math.max(8, Math.round(Math.min(width, height) * 0.05));
  const zones = [
    [0, 0, pad, pad],
    [width - pad, 0, width, pad],
    [0, height - pad, pad, height],
    [width - pad, height - pad, width, height],
  ];
  zones.forEach(([left, top, right, bottom]) => {
    for (let y = top; y < bottom; y += 2) {
      for (let x = left; x < right; x += 2) {
        const index = (y * width + x) * 4;
        samples.push([data[index], data[index + 1], data[index + 2]]);
      }
    }
  });
  samples.sort((a, b) => luminance(a[0], a[1], a[2]) - luminance(b[0], b[1], b[2]));
  const pick = samples[Math.floor(samples.length / 2)] || [255, 255, 255];
  const gray = luminance(pick[0], pick[1], pick[2]);
  return { r: pick[0], g: pick[1], b: pick[2], gray, isBright: gray >= 128 };
}

function sobel(gray, x, y, size) {
  const left = Math.max(0, x - 1);
  const right = Math.min(size - 1, x + 1);
  const top = Math.max(0, y - 1);
  const bottom = Math.min(size - 1, y + 1);
  const a = gray[top * size + left];
  const b = gray[top * size + x];
  const c = gray[top * size + right];
  const d = gray[y * size + left];
  const f = gray[y * size + right];
  const g = gray[bottom * size + left];
  const h = gray[bottom * size + x];
  const i = gray[bottom * size + right];
  const gx = -a + c - 2 * d + 2 * f - g + i;
  const gy = -a - 2 * b - c + g + 2 * h + i;
  return Math.sqrt(gx * gx + gy * gy) / 4;
}

function refineMask(mask, size) {
  const copy = mask.slice();
  for (let y = 1; y < size - 1; y += 1) {
    for (let x = 1; x < size - 1; x += 1) {
      const i = y * size + x;
      if (!copy[i]) continue;
      let neighbors = 0;
      for (let yy = -1; yy <= 1; yy += 1) {
        for (let xx = -1; xx <= 1; xx += 1) {
          neighbors += copy[(y + yy) * size + x + xx];
        }
      }
      mask[i] = neighbors >= 3 ? 1 : 0;
    }
  }

  const cleaned = mask.slice();
  for (let y = 2; y < size - 2; y += 1) {
    for (let x = 2; x < size - 2; x += 1) {
      const i = y * size + x;
      if (cleaned[i]) continue;
      let neighbors = 0;
      for (let yy = -2; yy <= 2; yy += 1) {
        for (let xx = -2; xx <= 2; xx += 1) {
          neighbors += cleaned[(y + yy) * size + x + xx];
        }
      }
      if (neighbors >= 13) mask[i] = 1;
    }
  }
}

// Downscaled analysis pass: finds the background color + a square crop
// tightly framing the subject (optionally excluding the plain background).
// Reads pixels via getImageData, which throws on a cross-origin image
// without CORS headers — callers must catch that and fall back gracefully.
function analyzeImage(image, removeBackground) {
  const naturalWidth = image.naturalWidth || image.width || 1;
  const naturalHeight = image.naturalHeight || image.height || 1;
  const scale = Math.min(1, SKETCH_WORK_SIZE / Math.max(naturalWidth, naturalHeight));
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));

  const work = document.createElement('canvas');
  work.width = width; work.height = height;
  const wctx = work.getContext('2d', { willReadFrequently: true });
  wctx.drawImage(image, 0, 0, width, height);
  const { data } = wctx.getImageData(0, 0, width, height);

  const background = sampleBackground(data, width, height);
  const threshold = background.isBright ? 38 : 46;

  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const distance = colorDistance(data, index, background);
      const gray = luminance(data[index], data[index + 1], data[index + 2]);
      const foreground = removeBackground
        ? isForeground(distance, gray, background, threshold)
        : data[index + 3] > 0;
      if (foreground) {
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < minX || maxY < minY) { minX = 0; minY = 0; maxX = width - 1; maxY = height - 1; }

  const padding = Math.round(Math.max(maxX - minX, maxY - minY) * 0.07);
  minX = clamp(minX - padding, 0, width - 1);
  minY = clamp(minY - padding, 0, height - 1);
  maxX = clamp(maxX + padding, 0, width - 1);
  maxY = clamp(maxY + padding, 0, height - 1);

  const boxWidth = maxX - minX + 1;
  const boxHeight = maxY - minY + 1;
  const side = Math.max(boxWidth, boxHeight);
  const centerX = minX + boxWidth / 2;
  const centerY = minY + boxHeight / 2;
  const cropX = Math.round(centerX - side / 2);
  const cropY = Math.round(centerY - side / 2);

  return {
    background,
    squareCrop: { x: cropX / scale, y: cropY / scale, size: side / scale },
  };
}

function drawCrop(image, ctx, crop, targetSize) {
  const naturalWidth = image.naturalWidth || image.width || 1;
  const naturalHeight = image.naturalHeight || image.height || 1;
  const sourceLeft = Math.max(0, crop.x);
  const sourceTop = Math.max(0, crop.y);
  const sourceRight = Math.min(naturalWidth, crop.x + crop.size);
  const sourceBottom = Math.min(naturalHeight, crop.y + crop.size);
  const sourceWidth = sourceRight - sourceLeft;
  const sourceHeight = sourceBottom - sourceTop;
  if (sourceWidth <= 0 || sourceHeight <= 0) return;

  const targetX = ((sourceLeft - crop.x) / crop.size) * targetSize;
  const targetY = ((sourceTop - crop.y) / crop.size) * targetSize;
  const targetWidth = (sourceWidth / crop.size) * targetSize;
  const targetHeight = (sourceHeight / crop.size) * targetSize;
  ctx.drawImage(image, sourceLeft, sourceTop, sourceWidth, sourceHeight, targetX, targetY, targetWidth, targetHeight);
}

// Renders a square, hand-sketched-looking version of `image` at `size`px.
// Reads/writes pixels directly, so — like analyzeImage — this throws on a
// tainted (cross-origin, no CORS) canvas.
function createPencilSketch(image, analysis, size, {
  contrast = DEFAULT_SKETCH_CONTRAST, shade = DEFAULT_SKETCH_SHADE, removeBackground = DEFAULT_REMOVE_BACKGROUND,
} = {}) {
  const crop = analysis.squareCrop;
  const scratch = document.createElement('canvas');
  scratch.width = size; scratch.height = size;
  const sctx = scratch.getContext('2d', { willReadFrequently: true });
  sctx.fillStyle = '#ffffff';
  sctx.fillRect(0, 0, size, size);
  drawCrop(image, sctx, crop, size);

  const imageData = sctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  const gray = new Float32Array(size * size);
  const mask = new Uint8Array(size * size);
  const contrastFactor = contrast / 72;
  const shadeFactor = shade / 100;
  const threshold = analysis.background.isBright ? 42 : 52;

  for (let i = 0; i < gray.length; i += 1) {
    const index = i * 4;
    const currentGray = luminance(data[index], data[index + 1], data[index + 2]);
    const distance = colorDistance(data, index, analysis.background);
    gray[i] = currentGray;
    mask[i] = removeBackground && !isForeground(distance, currentGray, analysis.background, threshold) ? 0 : 1;
  }

  refineMask(mask, size);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = y * size + x;
      const index = i * 4;
      if (!mask[i]) {
        data[index] = 255; data[index + 1] = 255; data[index + 2] = 255; data[index + 3] = 255;
        continue;
      }
      const edge = sobel(gray, x, y, size) * contrastFactor;
      const tone = Math.max(0, 255 - gray[i]) * shadeFactor;
      const paperGrain = ((x * 17 + y * 31) % 19) / 19;
      const hatch = (x + y * 2) % 9 === 0 ? tone * 0.12 : 0;
      const pencil = clamp(edge * 1.35 + tone * 0.85 + hatch + paperGrain * 7, 0, 235);
      const value = Math.round(255 - pencil);
      data[index] = value; data[index + 1] = value; data[index + 2] = value; data[index + 3] = 255;
    }
  }

  sctx.putImageData(imageData, 0, 0);
  return scratch;
}

// Draws the photo into the given box — pencil-sketch styled when enabled,
// otherwise a plain "contain" draw. Cross-origin photos without CORS
// headers taint the canvas as soon as pixels are read (analyzeImage /
// createPencilSketch above), so this transparently falls back to the plain
// draw instead of breaking the whole label.
function drawLabelPhoto(ctx, image, x, y, size, {
  sketchEnabled = DEFAULT_SKETCH_ENABLED,
  sketchContrast = DEFAULT_SKETCH_CONTRAST,
  sketchShade = DEFAULT_SKETCH_SHADE,
  removeBackground = DEFAULT_REMOVE_BACKGROUND,
} = {}) {
  if (!image) {
    ctx.strokeStyle = '#d4d9df';
    ctx.lineWidth = Math.max(2, Math.round(size * 0.006));
    ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
    return;
  }

  if (sketchEnabled) {
    try {
      const analysis = analyzeImage(image, removeBackground);
      const sketch = createPencilSketch(image, analysis, size, {
        contrast: sketchContrast, shade: sketchShade, removeBackground,
      });
      ctx.drawImage(sketch, x, y, size, size);
      return;
    } catch {
      // Tainted canvas (no CORS) or any other read failure — fall through
      // to a plain photo so the label still prints.
    }
  }

  drawImageContain(ctx, image, x, y, size, size);
}

// Draws the full label (order header text + print-ready photo, contained /
// centered + barcode) onto the given canvas at print resolution. `image` can
// be any drawImage-able source (HTMLImageElement, etc). The pencil-sketch
// photo style (see drawLabelPhoto above) needs to read pixels and will
// silently fall back to a plain photo if the source is cross-origin without
// CORS headers — either way, rendering/printing itself always works.
export function renderPrintLabel(canvas, {
  image, orderNumber, client, preset, showBarcodeText = true, dpi = EXPORT_DPI,
  sketchEnabled = DEFAULT_SKETCH_ENABLED,
  sketchContrast = DEFAULT_SKETCH_CONTRAST,
  sketchShade = DEFAULT_SKETCH_SHADE,
  removeBackground = DEFAULT_REMOVE_BACKGROUND,
}) {
  const width = mmToPx(preset.widthMm, dpi);
  const height = mmToPx(preset.heightMm, dpi);
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  const layout = getLabelLayout(width, height);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Header: order number (bold) + client (muted), centered.
  const headerCenterX = width / 2;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#000000';
  ctx.font = `700 ${Math.max(16, Math.round(layout.headerHeight * 0.62))}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillText(`#${orderNumber}`, headerCenterX, layout.headerY);

  if (client) {
    ctx.fillStyle = '#555555';
    ctx.font = `500 ${Math.max(12, Math.round(layout.headerHeight * 0.34))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillText(client, headerCenterX, layout.headerY + Math.round(layout.headerHeight * 0.58));
  }

  // Photo — pencil-sketch styled (when possible) or plain, contained +
  // centered within the photo box.
  drawLabelPhoto(ctx, image, layout.photoX, layout.photoY, layout.photoSize, {
    sketchEnabled, sketchContrast, sketchShade, removeBackground,
  });

  drawCode39Barcode(
    ctx, getOrderBarcodeValue(orderNumber),
    layout.barcodeX, layout.barcodeY, layout.barcodeWidth, layout.barcodeHeight,
    { showText: showBarcodeText },
  );

  return layout;
}

function drawImageContain(ctx, image, x, y, width, height) {
  const naturalWidth = image.naturalWidth || image.width || 1;
  const naturalHeight = image.naturalHeight || image.height || 1;
  const scale = Math.min(width / naturalWidth, height / naturalHeight);
  const drawWidth = naturalWidth * scale;
  const drawHeight = naturalHeight * scale;
  const drawX = x + (width - drawWidth) / 2;
  const drawY = y + (height - drawHeight) / 2;

  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

// Prints just `canvas`, sized to the real physical dimensions of `preset`
// (in mm), regardless of everything else on the page. Uses the
// visibility-based "print only this element" technique so app layout isn't
// disturbed and nothing needs to unmount.
//
// `canvas` may be a detached, off-DOM canvas (e.g. one created purely for
// printing) — the visibility trick only works on elements that are actually
// part of the document, so if it isn't attached yet we attach it ourselves
// (parked off-screen via a CSS class, never inline styles — inline styles
// would out-rank the print-time override below and leave the canvas parked
// off-page, printing a blank sheet) and detach it again once printing is done.
export function printLabelCanvas(canvas, preset) {
  const style = document.createElement('style');
  const printId = 'psw-print-label-target';
  const parkedClass = 'psw-print-label-canvas--parked';
  canvas.id = printId;

  const attachedHere = !canvas.isConnected;
  if (attachedHere) {
    canvas.classList.add(parkedClass);
    document.body.appendChild(canvas);
  }

  style.textContent = `
    .${parkedClass} { position: fixed; left: -10000px; top: 0; }
    @page { margin: 0; size: ${preset.widthMm}mm ${preset.heightMm}mm; }
    @media print {
      body * { visibility: hidden !important; }
      #${printId} {
        visibility: visible !important;
        position: fixed !important;
        top: 0 !important; left: 0 !important;
        width: ${preset.widthMm}mm !important; height: ${preset.heightMm}mm !important;
        margin: 0 !important; border: none !important; box-shadow: none !important;
      }
    }
  `;
  document.head.append(style);

  const cleanup = () => {
    style.remove();
    window.removeEventListener('afterprint', cleanup);
    if (attachedHere && canvas.parentNode) canvas.parentNode.removeChild(canvas);
  };
  window.addEventListener('afterprint', cleanup);
  // Safety net in case `afterprint` doesn't fire (some browsers/print-preview flows).
  setTimeout(cleanup, 10000);

  window.print();
}
